import { getFileUploadCategory } from '@/core/file-upload-category';
import {
  requestFileUpload,
  deleteFileUpload,
} from '@/core/svc-client/file-upload';
import { datadogLogs } from '@datadog/browser-logs';

export const FILE_REQUEST_STATUS = {
  PENDING: 0,
  PROCESSING: 1,
  SUCCESS: 2,
  FAILED: 3,
};

const BYTES_IN_MB = 1000000;

const SEPARATOR = '%';

export default {
  namespaced: true,

  state: {
    uploadStatuses: {},
    uploadProgress: {},
    uploadErrors: {},
    deleteStatuses: {},
    deleteErrors: {},
    fileStates: {},
  },

  getters: {
    /**
     * @returns {function({fieldName: String, fieldId: String}): String}
     */
    getFileId:
      () =>
      ({ fieldName, fieldId }) => {
        return `${fieldName}${SEPARATOR}${fieldId}`;
      },
  },

  mutations: {
    /**
     * @param state
     * @param fileId {String}
     */
    setUploadStarted(state, { fileId }) {
      state.uploadStatuses[fileId] = FILE_REQUEST_STATUS.PROCESSING;
    },

    /**
     * @param state
     * @param fileId {String}
     * @param progress {Integer}
     */
    setUploadProgress(state, { fileId, progress }) {
      state.uploadProgress[fileId] = progress;
      state.uploadStatuses[fileId] = FILE_REQUEST_STATUS.PROCESSING;
    },

    /**
     * @param state
     * @param fileId {String}
     */
    setUploadSuccess(state, { fileId }) {
      state.uploadStatuses[fileId] = FILE_REQUEST_STATUS.SUCCESS;
      state.deleteStatuses[fileId] = FILE_REQUEST_STATUS.PENDING;
      state.uploadErrors[fileId] = null;
      state.deleteErrors[fileId] = null;
    },

    /**
     * @param state
     * @param fileId {String}
     * @param error {Error}
     */
    setUploadError(state, { fileId, error }) {
      state.uploadStatuses[fileId] = FILE_REQUEST_STATUS.FAILED;
      state.uploadErrors[fileId] = error;
      state.deleteErrors[fileId] = null;
    },

    /**
     * @param state
     * @param fileId {String}
     * @param error {Error}
     */
    setDeleteSuccess(state, { fileId }) {
      state.fileStates[fileId] = null;
      state.deleteStatuses[fileId] = FILE_REQUEST_STATUS.SUCCESS;
      state.uploadStatuses[fileId] = FILE_REQUEST_STATUS.PENDING;
      state.deleteErrors[fileId] = null;
      state.uploadErrors[fileId] = null;
    },

    /**
     * @param state
     * @param fileId {String}
     * @param error {Error}
     */
    setDeleteError(state, { fileId, error }) {
      state.deleteStatuses[fileId] = FILE_REQUEST_STATUS.FAILED;
      state.deleteErrors[fileId] = error;
      state.uploadErrors[fileId] = null;
    },

    /**
     * @param state
     * @param fileId {String}
     * @param fileState {String}
     */
    setFileStates(state, { fileId, fileState }) {
      state.fileStates[fileId] = fileState;
    },
  },

  actions: {
    /**
     * A payload required for upload action
     * @typedef {Object} UploadPayload
     * @property {String} onboardingId
     * @property {String} filedName
     * @property {String} fileId
     * @property {String} fileLabel
     * @property {File} fileInfo
     */

    /**
     * @param state
     * @param commit
     * @param getters
     * @param dispatch
     * @param payload {UploadPayload}
     *
     */
    upload({ state, commit, getters, dispatch }, payload) {
      const { fieldName, fieldId, fileInfo } = payload;
      const fileId = getters.getFileId({ fieldName, fieldId });

      // reject when file is already in processing
      if (state.uploadStatuses[fileId] === FILE_REQUEST_STATUS.PROCESSING) {
        return;
      }

      commit('setUploadError', { fileId, error: null });

      const reader = new FileReader();
      reader.readAsDataURL(fileInfo);
      reader.onloadend = () => {
        // encapsulate logic for unit testing
        dispatch('uploadBase64File', {
          fileId,
          base64String: reader.result,
          uploadPayload: payload,
        });
      };
    },

    /**
     *
     * @param commit
     * @param fileId {String}
     * @param base64String {String}
     * @param uploadPayload {UploadPayload}
     */
    async uploadBase64File(
      { commit },
      { fileId, base64String, uploadPayload }
    ) {
      const { fieldName, fieldId, fileInfo, fileLabel, onboardingId } =
        uploadPayload;

      const { fileState, sizeInBytes, type, base64, uploadCategory } =
        mapFileUploadArguments({
          fileInfo,
          base64String,
          fieldName,
        });

      commit('setFileStates', { fileId, fileState });

      try {
        commit('setUploadStarted', fileId);

        const payload = {
          onboardingId,
          fieldId,
          fileLabel,
          sizeInBytes,
          type,
          base64,
          uploadCategory,
        };

        await requestFileUpload(payload, ({ loaded, total }) => {
          const progress = Math.round((loaded * 100) / total);
          commit('setUploadProgress', { fileId, progress });
        });

        commit('setUploadProgress', { fileId, progress: 100 });
        commit('setUploadSuccess', { fileId });
      } catch (e) {
        datadogLogs.logger.error(
          'Error uploading file',
          {
            metaData: {
              uploadingFile: base64String
                ? {
                    isFileNameEmpty: !fileLabel,
                    fieldName,
                    sizeInBytes: sizeInBytes,
                    type,
                  }
                : 'file object is empty',
            },
          },
          e
        );
        commit('setUploadError', { fileId, error: e });
      }
    },

    /**
     * @param commit
     * @param payload
     * @param payload.fileId {String}
     * @param payload.onboardingId {String}
     * @param payload.fieldId {String}
     * @returns {Promise<void>}
     */
    async delete({ commit }, payload) {
      const { fileId, onboardingId, fieldId } = payload;
      try {
        commit('setDeleteError', { fileId, error: null });
        await deleteFileUpload(onboardingId, fieldId);
        commit('setDeleteSuccess', { fileId });
      } catch (e) {
        commit('setDeleteError', { fileId, error: e });
      }
    },
  },
};

function mapFileUploadArguments({ fileInfo, base64String, fieldName }) {
  const fileState = {
    date: fileInfo.lastModifiedDate,
    name: fileInfo.name,
    sizeInBytes: fileInfo.size,
    sizeInMB: (fileInfo.size / BYTES_IN_MB).toFixed(2),
    type: fileInfo.type,
    base64: base64String,
  };

  return {
    fileState,
    sizeInBytes: fileState.sizeInBytes,
    type: fileState.type,
    base64: fileState.base64,
    uploadCategory: getFileUploadCategory(fieldName),
  };
}
